/**
 * A reading place, going to and from the learner's own repo.
 *
 * The rules are in `#/lib/flashbookProgressSync`, which is pure; this is the door
 * to the network. Three calls, and each of them has one interesting decision in it.
 *
 *  - **Nothing here throws at the reader.** A place that cannot be fetched and a
 *    place that was never written are the same thing to a learner opening a book:
 *    they start, or continue from wherever this device got to. So every failure
 *    ends as "no remote read" and "nothing written", which are the states the
 *    feature already has to handle. The one thing that is *not* swallowed is a
 *    successful write: the caller is told, so it can stop asking (see the hook's
 *    push, which retries a failure and stays quiet about a success).
 *
 *  - **A signed-out learner touches nothing.** No session means no repo to write
 *    in, and no request is made at all — reading offline is the device store's
 *    job, and a learner with no account must not have the app talking about them
 *    behind their back.
 *
 *  - **The record is cleared, not blanked.** A read that has ended has no place to
 *    carry, so finishing deletes the record rather than writing one that says
 *    "nothing is open": a record that exists is a record the learner's next device
 *    has to fetch, parse and dismiss.
 */
import {type AtpAgent} from '@atproto/api'

import {
  type FlashBookOpenRead,
  type FlashBookReadProgress,
} from '#/lib/flashbookProgress'
import {type FlashBookProgressIndex} from '#/lib/flashbookProgressStore'
import {
  openReadFromRecord,
  PROGRESS_COLLECTION,
  progressFromRecord,
  progressRecordFor,
  progressRecordKey,
} from '#/lib/flashbookProgressSync'

/** The repo a place lives in: the learner's, when they have one. */
function progressRepo(agent: AtpAgent | null): string | null {
  return agent?.session?.did ?? null
}

/**
 * The query key for the learner's places, which is one entry per account.
 *
 * Per account rather than per book: a screen draws many books at once, one key
 * means one cache entry, and every surface that asks in the same frame shares the
 * answer instead of asking again.
 */
export function flashBookPlacesQueryKey(repo: string | null) {
  return ['flashbook-places', repo ?? 'signed-out'] as const
}

/**
 * Every place this learner has left open, in one request — by book URI.
 *
 * Returns an empty index for signed-out users and on any error.
 */
export async function fetchFlashBookPlaces(
  agent: AtpAgent | null,
): Promise<FlashBookProgressIndex> {
  const repo = progressRepo(agent)
  if (!agent || !repo) return {}
  try {
    const res = await agent.com.atproto.repo.listRecords({
      repo,
      collection: PROGRESS_COLLECTION,
      limit: 100,
    })
    const places: FlashBookProgressIndex = {}
    for (const record of res.data.records) {
      const value = record.value as {bookUri?: unknown} | null | undefined
      const bookUri = value?.bookUri
      if (typeof bookUri !== 'string' || !bookUri) continue
      const progress = progressFromRecord(value, bookUri)
      if (progress) places[bookUri] = progress
    }
    return places
  } catch {
    return {}
  }
}

/**
 * The read this learner has open in a book, from their own repo.
 *
 * `null` covers all three of "nothing was ever written", "this device is signed
 * out" and "the request failed", deliberately: they are the same answer to the
 * only question the caller has, which is whether there is a place to take up.
 */
export async function fetchFlashBookProgress(
  agent: AtpAgent | null,
  bookUri: string,
): Promise<FlashBookOpenRead | null> {
  const repo = progressRepo(agent)
  if (!agent || !repo) return null
  try {
    const res = await agent.com.atproto.repo.getRecord({
      repo,
      collection: PROGRESS_COLLECTION,
      rkey: progressRecordKey(bookUri),
    })
    return openReadFromRecord(res.data.value, bookUri)
  } catch {
    return null
  }
}

/**
 * Write this device's place, or clear it when the read has ended.
 *
 * Reports whether it landed. `putRecord` replaces *or* creates, which is why a
 * device that has never written a place can write one without asking whether it
 * exists first — and why the same call serves a first save and a hundredth.
 */
export async function pushFlashBookProgress(
  agent: AtpAgent | null,
  progress: FlashBookReadProgress,
): Promise<boolean> {
  const repo = progressRepo(agent)
  if (!agent || !repo) return false
  const rkey = progressRecordKey(progress.bookUri)
  const record = progressRecordFor(progress)
  try {
    if (record) {
      await agent.com.atproto.repo.putRecord({
        repo,
        collection: PROGRESS_COLLECTION,
        rkey,
        record,
      })
    } else {
      /*
       * A record that is not there is the outcome asked for, so a refusal is not a
       * failure: a learner who finished a read on a device that had never written
       * one has nothing to clear, and the PDS says so by refusing.
       */
      await agent.com.atproto.repo.deleteRecord({
        repo,
        collection: PROGRESS_COLLECTION,
        rkey,
      })
    }
    return true
  } catch (e) {
    return record === null && isRecordNotFound(e)
  }
}

/**
 * Whether a PDS refused *because the record is not there*.
 *
 * The one refusal that is not a failure. A learner who finished a read on a device
 * that never wrote a place has nothing to clear, and the PDS says so by refusing —
 * which has achieved exactly what was asked. Counting it as a failure would leave
 * the hook retrying a delete that can never succeed, on every write for the rest of
 * the visit.
 */
function isRecordNotFound(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const refusal = error as {error?: unknown; status?: unknown}
  return refusal.error === 'RecordNotFound' || refusal.status === 404
}
